import type { DraftState, SheetRef, WizardFile, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'

/**
 * Browser-local recovery of what the server does not hold yet (detection results, file identities, the
 * sheet up to a size cap, column choices, upload state), per signed-in user; never FASTQ contents. Once
 * the order exists it is the source of truth. Every access tolerates storage being absent, full or blocked.
 */

export const DRAFT_SCHEMA_VERSION = 7
/** A draft not saved for this long is dropped: its order, if any, carries on in the classic view. */
export const STORED_DRAFT_TTL_MS = 14 * 24 * 60 * 60_000
/** A sheet larger than this, serialized, is not kept; it has to be dropped again after a reload. */
export const MAX_STORED_SHEET_CHARS = 500_000
const NAMESPACE = 'spcfy.orderWizard.'

const indexKey = (userId: string) => `${NAMESPACE}${userId}.drafts`

export type PersistedDraft = { schemaVersion: number; savedAt: string; draft: DraftState }

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> & Partial<Pick<Storage, 'key' | 'length'>>

const defaultStorage = (): StorageLike | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export const draftStorageKey = (userId: string, draftId: string) => `${NAMESPACE}${userId}.draft.${draftId}`

/** Only what survives a reload: an in-flight or queued upload has lost its file handle. */
const persistableFile = (file: WizardFile): WizardFile => {
  if (!file.upload || file.upload.status === 'uploaded') {
    return file
  }
  return { ...file, upload: { status: 'needs-readd', progress: 0, error: file.upload.error } }
}

/**
 * A write that was in flight when the page went away counts as not done: redoing it is safe, since every
 * write reads the order first, while a stored "pending" would hold the draft back forever.
 */
export const withoutInFlightWrites = (group: WizardSampleGroup): WizardSampleGroup => {
  const item = group.item.status === 'creating' ? { status: 'waiting' as const } : group.item
  const applied = group.applied?.status === 'pending' ? undefined : group.applied
  return item === group.item && applied === group.applied ? group : { ...group, item, applied }
}

export const serializeDraft = (draft: DraftState, now: Date = new Date()): string => {
  const files = Object.fromEntries(Object.entries(draft.files).map(([key, file]) => [key, persistableFile(file)]))
  const groups = Object.fromEntries(Object.entries(draft.groups).map(([key, group]) => [key, withoutInFlightWrites(group)]))
  const { sheet: fullSheet, ...rest } = draft
  delete rest.submitFlow
  delete rest.storageFailed
  delete rest.replacedSheet
  const sheetRef: SheetRef | undefined = fullSheet
    ? {
        fileKey: fullSheet.fileKey,
        name: fullSheet.name,
        mapping: fullSheet.mapping,
        extraColumns: fullSheet.extraColumns,
        revision: fullSheet.revision
      }
    : draft.sheetRef
  const sheet = fullSheet && JSON.stringify(fullSheet).length <= MAX_STORED_SHEET_CHARS ? fullSheet : undefined
  const persisted: PersistedDraft = {
    schemaVersion: DRAFT_SCHEMA_VERSION,
    savedAt: now.toISOString(),
    draft: { ...rest, sheet, sheetRef, files, groups, phase: draft.orderId ? 'active' : 'start', error: undefined }
  }
  return JSON.stringify(persisted)
}

/** `null` for anything unreadable or written by another schema version; the caller drops such an entry. */
export const parseDraft = (raw: string | null): DraftState | null => {
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedDraft>
    if (parsed.schemaVersion !== DRAFT_SCHEMA_VERSION || !parsed.draft || typeof parsed.draft.id !== 'string') {
      return null
    }
    return { ...parsed.draft, restored: true }
  } catch {
    return null
  }
}

const readIndex = (storage: StorageLike, userId: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(indexKey(userId)) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

const writeIndex = (storage: StorageLike, userId: string, ids: string[]): boolean => {
  try {
    storage.setItem(indexKey(userId), JSON.stringify([...new Set(ids)]))
    return true
  } catch {
    return false
  }
}

/* a full storage fails every save, several times a second while uploads report progress */
const failingDrafts = new Set<string>()

/** Whether the draft was kept: an entry the index does not name is never read back. */
export const saveDraft = (userId: string, draft: DraftState, storage: StorageLike | null = defaultStorage()): boolean => {
  if (!storage) {
    return false
  }
  let kept: boolean
  let failure: unknown
  try {
    storage.setItem(draftStorageKey(userId, draft.id), serializeDraft(draft))
    kept = writeIndex(storage, userId, [...readIndex(storage, userId), draft.id])
  } catch (error) {
    kept = false
    failure = error
  }
  if (kept) {
    failingDrafts.delete(draft.id)
  } else if (!failingDrafts.has(draft.id)) {
    failingDrafts.add(draft.id)
    console.info('Order wizard: the draft could not be kept in browser storage', failure ?? 'the draft index could not be written')
  }
  return kept
}

export const removeDraft = (userId: string, draftId: string, storage: StorageLike | null = defaultStorage()) => {
  if (!storage) {
    return
  }
  try {
    storage.removeItem(draftStorageKey(userId, draftId))
  } catch (error) {
    console.warn('Order wizard: could not remove the local draft', error)
  }
  writeIndex(
    storage,
    userId,
    readIndex(storage, userId).filter((id) => id !== draftId)
  )
}

const wizardKeys = (storage: StorageLike, test: (key: string) => boolean): string[] => {
  const keys: string[] = []
  for (let i = 0; i < (storage.length ?? 0); i++) {
    const key = storage.key?.(i)
    if (key && test(key)) {
      keys.push(key)
    }
  }
  return keys
}

const removeKeys = (storage: StorageLike, keys: readonly string[]) => {
  keys.forEach((key) => {
    try {
      storage.removeItem(key)
    } catch {
      /* storage refused; nothing else to do */
    }
  })
}

/** Every wizard entry of every user, for logging out. */
export const clearOrderWizardStorage = (storage: StorageLike | null = defaultStorage()) => {
  if (!storage) {
    return
  }
  try {
    removeKeys(
      storage,
      wizardKeys(storage, (key) => key.startsWith(NAMESPACE))
    )
  } catch (error) {
    console.warn('Order wizard: could not clear local drafts', error)
  }
}

/** `expired`: not opened within the TTL; otherwise the entry could not be read back. */
export type UnrestorableDraft = { draftId: string; orderId?: string; reason?: 'expired' }

/** The order a draft entry names, read leniently so a draft the wizard cannot restore still leads to its order. */
const orderIdIn = (raw: string | null): string | undefined => {
  try {
    const orderId: unknown = (JSON.parse(raw ?? '') as { draft?: { orderId?: unknown } })?.draft?.orderId
    return typeof orderId === 'string' ? orderId : undefined
  } catch {
    return undefined
  }
}

const savedAtOf = (raw: string | null): number | undefined => {
  try {
    const savedAt: unknown = (JSON.parse(raw ?? '') as { savedAt?: unknown })?.savedAt
    return typeof savedAt === 'string' ? Date.parse(savedAt) : undefined
  } catch {
    return undefined
  }
}

const readEntry = (storage: StorageLike, key: string): string | null => {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

/** The index without the drafts not saved within the TTL, which leave storage on the way and are reported. */
const liveIndex = (storage: StorageLike, userId: string, now: number): { live: string[]; expired: UnrestorableDraft[] } => {
  const ids = readIndex(storage, userId)
  const expired: UnrestorableDraft[] = []
  for (const id of ids) {
    const raw = readEntry(storage, draftStorageKey(userId, id))
    const savedAt = savedAtOf(raw)
    if (savedAt !== undefined && now - savedAt > STORED_DRAFT_TTL_MS) {
      const orderId = orderIdIn(raw)
      expired.push({ draftId: id, ...(orderId ? { orderId } : {}), reason: 'expired' })
      removeDraft(userId, id, storage)
    }
  }
  return { live: ids.filter((id) => !expired.some((entry) => entry.draftId === id)), expired }
}

/** The orders the user's stored drafts hold, read leniently: also from entries this version cannot restore. */
export const storedOrderIds = (userId: string, storage: StorageLike | null = defaultStorage(), now = Date.now()): string[] => {
  if (!storage) {
    return []
  }
  return liveIndex(storage, userId, now).live.flatMap((id) => {
    try {
      const orderId = orderIdIn(storage.getItem(draftStorageKey(userId, id)))
      return orderId ? [orderId] : []
    } catch {
      return []
    }
  })
}

const DRAFT_KEY = new RegExp(`^${NAMESPACE.replace(/\./g, '\\.')}(.+)\\.draft\\.(.+)$`)

/** Other users' drafts not saved within the TTL leave this browser too: nobody else would ever clear them. */
const pruneOthersExpired = (storage: StorageLike, userId: string, now: number) => {
  for (const key of wizardKeys(storage, (candidate) => DRAFT_KEY.test(candidate))) {
    const [, owner, draftId] = DRAFT_KEY.exec(key) ?? []
    const savedAt = savedAtOf(readEntry(storage, key))
    if (owner && owner !== userId && savedAt !== undefined && now - savedAt > STORED_DRAFT_TTL_MS) {
      removeDraft(owner, draftId, storage)
    }
  }
}

/** Every readable draft. Unreadable and outdated entries are removed, and reported so the classic view can take over. */
export const loadDrafts = (
  userId: string,
  storage: StorageLike | null = defaultStorage(),
  now = Date.now()
): { drafts: DraftState[]; unrestorable: UnrestorableDraft[] } => {
  const result: { drafts: DraftState[]; unrestorable: UnrestorableDraft[] } = { drafts: [], unrestorable: [] }
  if (!storage) {
    return result
  }
  try {
    pruneOthersExpired(storage, userId, now)
  } catch (error) {
    console.info('Order wizard: could not prune expired drafts', error)
  }
  const { live, expired } = liveIndex(storage, userId, now)
  result.unrestorable.push(...expired)
  for (const id of live) {
    const raw = readEntry(storage, draftStorageKey(userId, id))
    const draft = parseDraft(raw)
    if (draft) {
      result.drafts.push(draft)
    } else {
      result.unrestorable.push({ draftId: id, orderId: orderIdIn(raw) })
      removeDraft(userId, id, storage)
    }
  }
  return result
}
